import React, { useEffect, useMemo, useState } from "react";
import { Box, Button, Flex, Grid, Link, Modal, ModalBody, ModalContent, ModalOverlay, Text } from "@chakra-ui/react";
import { useLocation, useNavigate } from "react-router-dom";
import { useUser } from "../../contexts/UserContext";
import { isConsentTarget } from "../../lib/policyConsentRules";
import { isNativePlatform } from "../../lib/platformUtils";
import { setRolloutGateOpen } from "../../lib/rolloutGate";
import useTierRolloutNotice, { ackTierRolloutNotice } from "../../hooks/useTierRolloutNotice";
import {
  buildInterstitialCopy, shouldShowInterstitial, readForcedRollout, FORCED_STATES,
} from "../../lib/tierRolloutNotice";
import { tt } from "../../i18n/tt";

// ============================================================================
// 등급제 배포 안내 — 첫 로그인 전면 안내 (시안 C 확정, 대표 결정 2026-09-07)
// 기획 docs/ops/tier-rollout-communication-plan.md §3 · 시안 docs/design/tier-rollout-communication-variants.html
//
// 노출: UserLayout 에 마운트. 크리에이터(isConsentTarget) · 영상권리사 아님 · 대리 접속 아님 · 서버에 확인 기록 없음.
//   RPC get_tier_rollout_notice_state 가 실패하면 띄우지 않는다 — 안내가 앱을 막으면 안 된다(PolicyConsentGate 와 같은 원칙).
// 닫힘: 「확인했어요」/「자세히 보기」만. ESC·바깥 클릭 없음. 확인 = ack_tier_rollout_notice(서버 1회 · 기기 무관).
// 자극은 크기와 대비로: 어두운 전면 + 「10월 1일부터」(등급제 시작일) 가 화면에서 가장 큰 글자. 둘째 줄이 「멤버십 구독료 결제만 10월 31일까지 유예 ·
// 첫 결제 11월 1일」(2026-09-22 대표 정정 — 종전 「10월 31일까지 아무것도 바뀌지 않아요」 폐기). 문장은 tierRolloutNotice 사전에서만.
// QA: ?rollout=paid|exempt|rookie|pre|dormant|new|none — 강제 미리보기(확인 기록 안 함).
// ============================================================================

const isImpersonating = () => {
  try {
    return Boolean(window.localStorage.getItem("impersonateEmail") || window.localStorage.getItem("adminViewImpersonateEmail"));
  } catch {
    return false;
  }
};

export default function TierRolloutInterstitial() {
  const { userInfo } = useUser();
  const location = useLocation();
  const navigate = useNavigate();
  const email = userInfo?.email;
  const role = userInfo?.role;
  const forced = readForcedRollout(location.search);
  const target = Boolean(email) && isConsentTarget(role) && !isImpersonating();

  const { notice, loading, markAcked } = useTierRolloutNotice(email, { enabled: target && !forced });
  const state = forced ? FORCED_STATES[forced] : notice;
  const open = forced ? true : shouldShowInterstitial(state, { isTarget: target });

  const copy = useMemo(() => (state ? buildInterstitialCopy(state) : null), [state]);
  // D-카운트는 첫 노출 시점 값으로 고정(매 렌더 흔들리지 않게)
  const [days, setDays] = useState(null);
  useEffect(() => {
    if (open && copy && days === null) setDays(copy.days);
  }, [open, copy, days]);
  const [busy, setBusy] = useState(false);
  const [closed, setClosed] = useState(false);
  useEffect(() => { setClosed(false); }, [email]);

  // Android 하드웨어 뒤로가기 — 확인 버튼으로만 닫히는 화면이라 리스너를 잡아 아무것도 하지 않는다(PolicyConsentModal 과 같은 패턴).
  // 리스너가 없으면 뒤로가기가 WebView 히스토리를 움직여 안내가 확인 기록 없이 사라진다.
  const showing = open && Boolean(copy) && !closed;
  // 안내 겹침 방지 — 이 화면이 덮고 있는 동안(그리고 뜰지 아직 조회 중인 동안) 홈의 튜토리얼·코치마크·승인 대기 바는 기다린다.
  // 조회가 끝나 안 뜨기로 되면 게이트가 닫혀 홈 안내가 평소처럼 시작한다(2026-09-07 대표 실측 겹침 수정).
  const pending = target && !forced && loading && !closed;
  useEffect(() => {
    setRolloutGateOpen(showing || pending);
    return () => setRolloutGateOpen(false);
  }, [showing, pending]);
  useEffect(() => {
    if (!showing || !isNativePlatform()) return undefined;
    let handle;
    import("@capacitor/app").then(({ App }) => {
      handle = App.addListener("backButton", () => {});
    }).catch(() => {});
    return () => { if (handle) handle.then ? handle.then((h) => h.remove()) : handle.remove(); };
  }, [showing]);

  if (!showing) return null;

  const finish = async (variant, to) => {
    if (busy) return;
    setBusy(true);
    if (!forced) {
      try {
        await ackTierRolloutNotice({ segment: copy.segment, variant });
        markAcked();
      } catch (err) {
        // 기록 실패는 사용자를 가두는 이유가 못 된다 — 다음 진입에 다시 뜬다
        console.warn("등급제 안내 확인 기록 실패:", err?.message || err);
      }
    }
    setBusy(false);
    setClosed(true);
    if (to) navigate(to);
  };

  return (
    <Modal isOpen onClose={() => {}} size="full" closeOnEsc={false} closeOnOverlayClick={false} autoFocus trapFocus motionPreset="none">
      <ModalOverlay bg="neutral.950" />
      <ModalContent bg="neutral.900" color="neutral.50" borderRadius={0} m={0} minH="100dvh" data-testid="tier-rollout-interstitial" data-segment={copy.segment}>
        <ModalBody display="flex" alignItems="center" justifyContent="center" px={{ base: 5, md: 12 }} py={{ base: 8, md: 12 }}>
          <Grid w="100%" maxW="1040px" templateColumns={{ base: "1fr", md: "1.15fr 1fr" }} gap={{ base: 6, md: 10 }} alignItems="start">
            <Box>
              <Text fontSize="xs" letterSpacing="0.12em" textTransform="uppercase" color="brand.300" fontWeight="700">
                {copy.kicker}
              </Text>
              <Text as="h1" mt={2} mb={1} fontWeight="900" lineHeight="1" letterSpacing="-0.03em" fontSize={{ base: "52px", md: "78px" }} sx={{ fontVariantNumeric: "tabular-nums" }}>
                {copy.datePrefix ? (
                  <Text as="span" fontSize={{ base: "24px", md: "34px" }} fontWeight="700" letterSpacing="-0.01em" mr={2}>{copy.datePrefix}</Text>
                ) : null}
                {copy.dateBig}
                {copy.dateSuffix ? (
                  <Text as="span" fontSize={{ base: "24px", md: "34px" }} fontWeight="700" letterSpacing="-0.01em" ml={1}>{copy.dateSuffix}</Text>
                ) : null}
              </Text>
              <Text fontSize={{ base: "xl", md: "2xl" }} fontWeight="700" mb={2}>{copy.headline}</Text>
              <Text fontSize={{ base: "md", md: "lg" }} color="neutral.300" mb={4}>
                <Text as="b" color="accent.200" fontWeight="700">{copy.calm.lead}</Text>
                {copy.calm.rest ? ` ${copy.calm.rest}` : ""}
              </Text>
              {typeof days === "number" && days > 0 && copy.segment !== "new" && (
                <Flex display="inline-flex" align="baseline" gap={2} px={3} py={1} border="1px solid" borderColor="neutral.700" borderRadius="full" fontSize="sm" color="neutral.300" sx={{ fontVariantNumeric: "tabular-nums" }}>
                  {tt("a.94cb2ff4", "{{v0}}까지", { v0: copy.dateBig })} <Text as="b" fontSize="lg" color="brand.300">D-{days}</Text>
                </Flex>
              )}
              <Flex gap={3} mt={5} flexWrap="wrap" display={{ base: "none", md: "flex" }}>
                <Button size="lg" bg="brand.300" color="neutral.900" fontWeight="900" _hover={{ bg: "brand.200" }} isLoading={busy} onClick={() => finish("C")}>
                  {copy.ctaPrimary}
                </Button>
                <Button size="lg" variant="outline" borderColor="neutral.600" color="neutral.50" _hover={{ bg: "neutral.800" }} isDisabled={busy} onClick={() => finish("C:detail", "/tier-guide")}>
                  {copy.ctaSecondary}
                </Button>
              </Flex>
              <Text mt={3} fontSize="xs" color="neutral.400" display={{ base: "none", md: "block" }}>{copy.fine}</Text>
              {copy.help && (
                <Text mt={2} fontSize="sm" color="neutral.300" display={{ base: "none", md: "block" }}>
                  {copy.help.text}{" "}
                  <Link href={copy.help.url} isExternal color="brand.300" fontWeight="700" data-testid="tier-rollout-help">{copy.help.label} ↗</Link>
                </Text>
              )}
            </Box>

            <Flex direction="column" gap={3}>
              {copy.cards.map((c, i) => (
                <Box key={c.k} bg="neutral.800" border="1px solid" borderColor={i === 0 ? "brand.300" : "neutral.700"} boxShadow={i === 0 ? "0 0 0 3px rgba(77,202,188,.18)" : "none"} borderRadius="10px" px={4} py={3}>
                  <Text fontSize="2xs" letterSpacing="0.06em" textTransform="uppercase" color="brand.300" fontWeight="700">{c.k}</Text>
                  <Text fontSize={{ base: "md", md: "lg" }} fontWeight="700" lineHeight="1.35" my={0.5}>
                    {c.v}
                    {c.em && <Text as="em" fontStyle="normal" color="accent.200">{c.em}</Text>}
                    {c.tail || ""}
                  </Text>
                  {c.s && <Text fontSize="sm" color="neutral.300">{c.s}</Text>}
                </Box>
              ))}
              <Flex direction="column" gap={2} mt={1} display={{ base: "flex", md: "none" }}>
                <Button size="lg" bg="brand.300" color="neutral.900" fontWeight="900" _hover={{ bg: "brand.200" }} isLoading={busy} onClick={() => finish("C")}>
                  {copy.ctaPrimary}
                </Button>
                <Button size="md" variant="ghost" color="neutral.300" _hover={{ bg: "neutral.800" }} isDisabled={busy} onClick={() => finish("C:detail", "/tier-guide")}>
                  {copy.ctaSecondary}
                </Button>
                <Text fontSize="xs" color="neutral.400" textAlign="center">{copy.fine}</Text>
                {copy.help && (
                  <Text fontSize="sm" color="neutral.300" textAlign="center">
                    {copy.help.text}{" "}
                    <Link href={copy.help.url} isExternal color="brand.300" fontWeight="700">{copy.help.label} ↗</Link>
                  </Text>
                )}
              </Flex>
            </Flex>
          </Grid>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}
